import type Player from 'video.js/dist/types/player'
// Registers the qualityLevels() plugin on the videojs instance. Imported here,
// the single module both surfaces depend on, so it loads exactly once.
import 'videojs-contrib-quality-levels'
import { QualityLevelInfo } from './surfaceTypes'

// The actively-playing HLS rendition, as reported by VHS's current media
// playlist. Either field may be absent depending on what the master playlist
// declared.
export interface PlayingRendition {
  height?: number
  width?: number
  bitrate?: number
}

// The name a viewer knows a rung by, derived from frame WIDTH.
//
// WHY WIDTH, NOT HEIGHT. A 360 master is 2:1 equirectangular, so its 4K rung
// is 3840x1920 — and labelling by height printed that as "1920p", which names
// no tier anyone has heard of and hid the 4K rung in plain sight. Width is the
// number the tier is actually named after (3840 IS 4K), and it is the same
// number for the 16:9 flat fallback, so one table serves both surfaces.
//
// Thresholds, not equalities: encoders round widths to even macroblocks and a
// 4K rung has shipped as 3840 and as 3820. Each entry claims everything from
// its width up to the next one.
const LADDER: Array<{ minWidth: number; label: string }> = [
  { minWidth: 7000, label: '8K' },
  { minWidth: 3500, label: '4K' },
  { minWidth: 2400, label: '1440p' },
  { minWidth: 1800, label: '1080p' },
  { minWidth: 1200, label: '720p' },
  { minWidth: 800, label: '480p' },
  { minWidth: 600, label: '360p' },
  { minWidth: 400, label: '240p' },
]

/**
 * Label for one rung of the quality ladder, e.g. `4K` or `720p`.
 *
 * Falls back to `<height>p` when the master playlist declared no width — a
 * poorer name, but the only honest one available, and better than inventing a
 * tier from a number we do not have.
 */
export function qualityLabel(level: { width?: number; height?: number }): string {
  const width = level.width ?? 0
  const rung = LADDER.find((r) => width >= r.minWidth)
  if (rung) return rung.label
  if (level.height) return `${level.height}p`
  return width > 0 ? `${width}w` : 'Auto'
}

// Choose the index in `levels` that corresponds to the rendition VHS is
// actually playing. The quality-levels plugin's `selectedIndex` reflects the
// *enabled* selection, which during ABR (auto) does not track the rendition
// that bandwidth has dropped us onto — so we prefer the playing rendition and
// only fall back to selectedIndex when we can't match it.
export function pickActiveIndex(
  levels: QualityLevelInfo[],
  playing: PlayingRendition | null,
  selectedIndex: number,
): number {
  if (playing) {
    if (playing.height) {
      const exact = levels.findIndex(
        (l) => l.height === playing.height && (!playing.bitrate || l.bitrate === playing.bitrate),
      )
      if (exact >= 0) return exact
      const byHeight = levels.findIndex((l) => l.height === playing.height)
      if (byHeight >= 0) return byHeight
    }
    if (playing.bitrate) {
      const byBitrate = levels.findIndex((l) => l.bitrate === playing.bitrate)
      if (byBitrate >= 0) return byBitrate
    }
  }
  return selectedIndex
}

type QualityLevelsList = {
  length: number
  selectedIndex: number
  [index: number]: { width?: number; height?: number; bitrate?: number; enabled: boolean }
  on(event: string, handler: () => void): void
  off(event: string, handler: () => void): void
}

// VHS's playlist loader for the current source. Recreated from scratch every
// time the player is given a new source.
type VhsPlaylists = {
  media?: () => unknown
  on?: (event: string, handler: () => void) => void
  off?: (event: string, handler: () => void) => void
}

type OnQualityLevelsChange = (levels: QualityLevelInfo[], activeIndex: number) => void

// Wraps a video.js player's quality-levels plugin and reports level state to
// React. Shared by the sphere (VideoPlayer360Controller) and flat (FlatSurface)
// surfaces so both get an accurate, auto-switch-aware bitrate readout.
//
// When VHS is not driving playback (e.g. native HLS on iOS, where there is no
// MSE), `qualityLevels()` stays empty and this controller simply reports no
// levels — the UI then hides its bitrate menu.
export class QualityLevelController {
  private qualityLevels: QualityLevelsList | null = null
  private pollInterval: ReturnType<typeof setInterval> | null = null
  private notify = () => this.emit()
  private resync = () => this.handleSourceChange()
  private vhsPlaylists: VhsPlaylists | null = null

  constructor(
    private player: Player,
    private onChange: OnQualityLevelsChange,
  ) {
    // The controller outlives any single source: the core keeps one player and
    // calls player.src() to switch views, so re-bind on every source change.
    try {
      this.player.on('loadstart', this.resync)
    } catch {
      // a player that cannot take listeners gives us a levels-only controller
    }
    this.bindLevels()
  }

  // The quality-levels plugin creates its list once per player, so the object
  // identity normally survives a source change — but a controller built before
  // the plugin had a list still needs to pick one up later.
  private bindLevels(): void {
    let levels: QualityLevelsList | null = null
    try {
      levels =
        (this.player as unknown as { qualityLevels?: () => QualityLevelsList }).qualityLevels?.() ?? null
    } catch {
      levels = null
    }
    if (levels !== this.qualityLevels) {
      this.detachLevelListeners()
      this.qualityLevels = levels
      if (levels) {
        levels.on('addqualitylevel', this.notify)
        levels.on('change', this.notify)
      }
    }
    if (!this.qualityLevels) return
    // VHS does not always fire a quality-levels event when ABR switches the
    // active rendition, so poll as a backstop.
    if (!this.pollInterval) this.pollInterval = setInterval(this.notify, 2000)
  }

  // A new source means a new VHS handler: the old playlist loader is dead and
  // VHS has emptied the level list under us. Re-bind everything, drop any
  // manual rendition pin (the new ladder is a different ladder), and report the
  // new state — which is legitimately "no levels yet" until the new manifest
  // has been parsed.
  private handleSourceChange(): void {
    this.bindLevels()
    this.syncVhsListener()
    this.enableAllLevels()
    this.emit()
  }

  private vhs(): { playlists?: VhsPlaylists } | null {
    try {
      const tech = (this.player as unknown as { tech?: (o: { IWillNotUseThisInPlugins: boolean }) => unknown }).tech?.({
        IWillNotUseThisInPlugins: true,
      })
      return (tech as { vhs?: { playlists?: VhsPlaylists } })?.vhs ?? null
    } catch {
      return null
    }
  }

  // VHS's playlist loader emits 'mediachange' the moment the active rendition
  // switches — the most direct signal for an auto down/up-switch. The loader is
  // replaced wholesale on a source change, so bind to whichever one is current
  // and let go of the previous one.
  private syncVhsListener(): void {
    const playlists = this.vhs()?.playlists ?? null
    if (playlists === this.vhsPlaylists) return
    this.detachVhsListener()
    if (playlists?.on) {
      playlists.on('mediachange', this.notify)
      this.vhsPlaylists = playlists
    }
  }

  private detachVhsListener(): void {
    try {
      this.vhsPlaylists?.off?.('mediachange', this.notify)
    } catch {
      // tech may already be disposed
    }
    this.vhsPlaylists = null
  }

  private detachLevelListeners(): void {
    try {
      this.qualityLevels?.off('addqualitylevel', this.notify)
      this.qualityLevels?.off('change', this.notify)
    } catch {
      // player/tech may already be disposed
    }
  }

  private enableAllLevels(): void {
    if (!this.qualityLevels) return
    for (let i = 0; i < this.qualityLevels.length; i++) this.qualityLevels[i].enabled = true
  }

  private playingRendition(): PlayingRendition | null {
    const media = this.vhs()?.playlists?.media?.() as
      | { attributes?: { RESOLUTION?: { width?: number; height?: number }; BANDWIDTH?: number } }
      | undefined
    if (!media?.attributes) return null
    const res = media.attributes.RESOLUTION
    return { height: res?.height, width: res?.width, bitrate: media.attributes.BANDWIDTH }
  }

  private emit(): void {
    // Self-healing backstop: at loadstart the new VHS handler may not exist
    // yet, so the poll picks its playlist loader up on the next tick.
    this.syncVhsListener()
    if (!this.qualityLevels) return
    const levels: QualityLevelInfo[] = []
    for (let i = 0; i < this.qualityLevels.length; i++) {
      const ql = this.qualityLevels[i]
      levels.push({
        index: i,
        width: ql.width || 0,
        height: ql.height || 0,
        bitrate: ql.bitrate || 0,
        enabled: ql.enabled,
      })
    }
    const activeIndex = pickActiveIndex(levels, this.playingRendition(), this.qualityLevels.selectedIndex ?? -1)
    this.onChange(levels, activeIndex)
  }

  // 'auto' re-enables all renditions for ABR; a numeric index pins a single
  // rendition. Disabling all then enabling the target forces VHS's
  // fastQualityChange via the false→true transition.
  setQualityLevel(index: number | 'auto'): void {
    if (!this.qualityLevels) return
    // A pin can outlive the ladder it was chosen from (a source change empties
    // the list), so an out-of-range index is a no-op rather than a throw.
    if (index !== 'auto' && !this.qualityLevels[index]) return
    if (index === 'auto') {
      this.enableAllLevels()
    } else {
      for (let i = 0; i < this.qualityLevels.length; i++) this.qualityLevels[i].enabled = false
      this.qualityLevels[index].enabled = true
    }
    this.emit()
  }

  dispose(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
    try {
      this.player.off('loadstart', this.resync)
    } catch {
      // player may already be disposed
    }
    this.detachLevelListeners()
    this.detachVhsListener()
    this.qualityLevels = null
  }
}
